import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  STATION_OPERATION_META,
  STATION_REGISTRY_META,
} from '@goelectric/ui-components';
import type {
  AdminStationSummary,
  PageMeta,
  StationOperationStatus,
  StationRegistryStatus,
} from '@goelectric/shared-types';
import {
  CONNECTOR_STANDARD_LABELS,
  OCPP_VERSION_LABELS,
  stationDisplayName,
} from '../utils/format';
import type { StationFilters } from '../hooks/useStations';
import { StationOperationBadge, StationRegistryBadge } from './StationBadges';

/** Debounce de la búsqueda antes de golpear el backend (server-side). */
const SEARCH_DEBOUNCE_MS = 300;

const REGISTRY_OPTIONS: { value: StationRegistryStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Todos los registros' },
  ...(['PRE_REGISTERED', 'SYNCHRONIZED', 'ERROR', 'DISABLED'] as const).map((value) => ({
    value,
    label: STATION_REGISTRY_META[value].label,
  })),
];

const OPERATION_OPTIONS: { value: StationOperationStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Toda la operación' },
  ...(['DISCONNECTED', 'AVAILABLE', 'CHARGING', 'OCCUPIED', 'UNAVAILABLE'] as const).map((value) => ({
    value,
    label: STATION_OPERATION_META[value].label,
  })),
];

/** Marca · modelo del equipo; cae a `—` si el CRM aún no los tiene. */
function brandModel(station: AdminStationSummary): string {
  return [station.vendor, station.model].filter(Boolean).join(' · ') || '—';
}

/** Un tag por estándar de conector; el total solo se muestra si agrega algo. */
function ConnectorsCell({ station }: { station: AdminStationSummary }) {
  const labels = [...new Set(station.connectorStandards)].map(
    (standard) => CONNECTOR_STANDARD_LABELS[standard],
  );
  const count = station.totalConnectorsCount;

  if (labels.length === 0) {
    return (
      <Typography.Text type="secondary">
        {count > 0 ? `${count} conector${count === 1 ? '' : 'es'}` : '—'}
      </Typography.Text>
    );
  }

  return (
    <Space size={[4, 4]} wrap>
      {labels.map((label) => (
        <Tag key={label} bordered={false} style={{ marginInlineEnd: 0 }}>
          {label}
        </Tag>
      ))}
      {count > labels.length && (
        <Typography.Text type="secondary">×{count}</Typography.Text>
      )}
    </Space>
  );
}

export interface StationTableProps {
  stations: AdminStationSummary[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: StationFilters;
  onFiltersChange: (filters: StationFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (stationId: string) => void;
}

/** `ACTIVE` es el default del listado: no cuenta como filtro activo del empty-state. */
function hasActiveFilters(filters: StationFilters): boolean {
  return Boolean(filters.q || filters.registryStatus || filters.operationStatus);
}

/**
 * Tabla de Cargadores (guía de estilo §8): búsqueda, filtro por estado, paginación
 * server-side (con el `meta` del contrato), empty-state con acción sugerida, badges
 * de estado + sincronización y columna "Ver". Toda la fila es clickeable y navega al
 * detalle (`/cargadores/:stationId`), igual que Aliados y Ubicaciones.
 */
export function StationTable({
  stations,
  meta,
  loading,
  error,
  filters,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
}: StationTableProps) {
  const [query, setQuery] = useState(filters.q ?? '');

  const columns = useMemo<ColumnsType<AdminStationSummary>>(
    () => [
      {
        title: 'Identidad',
        key: 'identity',
        fixed: 'left',
        width: 280,
        render: (_, station) => (
          <Space direction="vertical" size={0}>
            {station.location.name?.trim() && (
              <Typography.Text strong>{station.location.name.trim()}</Typography.Text>
            )}
            <Typography.Text
              type="secondary"
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {station.id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Marca / modelo',
        key: 'brandModel',
        width: 180,
        render: (_, station) => brandModel(station),
      },
      {
        title: 'Protocolo',
        key: 'protocol',
        width: 110,
        render: (_, station) => OCPP_VERSION_LABELS[station.protocol],
      },
      {
        title: 'Potencia máx',
        key: 'maxPowerKw',
        width: 120,
        render: (_, station) => `${station.maxPowerKw} kW`,
      },
      {
        title: 'Conectores',
        key: 'connectors',
        width: 220,
        render: (_, station) => <ConnectorsCell station={station} />,
      },
      {
        title: 'Registro',
        key: 'registryStatus',
        width: 160,
        render: (_, station) => <StationRegistryBadge status={station.registryStatus} />,
      },
      {
        title: 'Operación',
        key: 'operationStatus',
        width: 170,
        render: (_, station) => (
          <StationOperationBadge
            status={station.operationStatus ?? null}
            reason={station.operationReason ?? null}
          />
        ),
      },
      {
        title: 'Ver',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, station) => (
          <Button
            size="small"
            aria-label={`Ver ${stationDisplayName(station)}`}
            onClick={() => onSelect(station.id)}
          >
            Ver
          </Button>
        ),
      },
    ],
    [onSelect],
  );

  // Sincroniza la búsqueda con el backend con debounce (evita una request por tecla).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      if ((filters.q ?? '') !== next) {
        onFiltersChange({ ...filters, q: next || undefined });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, filters, onFiltersChange]);

  const filtered = hasActiveFilters(filters);

  const clearFilters = () => {
    setQuery('');
    onFiltersChange({});
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por ChargePoint ID, marca o modelo"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 320 }}
          aria-label="Buscar cargadores"
        />
        <Select<StationRegistryStatus | 'ALL'>
          value={filters.registryStatus ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              registryStatus: value === 'ALL' ? undefined : value,
            })
          }
          style={{ width: 190 }}
          aria-label="Filtrar por estado del registro"
          options={REGISTRY_OPTIONS}
        />
        <Select<StationOperationStatus | 'ALL'>
          value={filters.operationStatus ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              operationStatus: value === 'ALL' ? undefined : value,
            })
          }
          style={{ width: 190 }}
          aria-label="Filtrar por estado operativo"
          options={OPERATION_OPTIONS}
        />
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los cargadores"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table<AdminStationSummary>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={stations}
          scroll={{ x: 1310 }}
          rowClassName="cargadores-row"
          onRow={(station) => ({ onClick: () => onSelect(station.id) })}
          pagination={{
            current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} cargadores`,
            onChange: (page) => onOffsetChange((page - 1) * meta.limit),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  filtered ? 'Sin cargadores para los filtros' : 'Aún no hay cargadores'
                }
              >
                {filtered ? (
                  <Button onClick={clearFilters}>Limpiar filtros</Button>
                ) : (
                  <Button type="primary" onClick={onCreate}>
                    Crear el primer cargador
                  </Button>
                )}
              </Empty>
            ),
          }}
        />
      )}
    </div>
  );
}
